import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Search, X, FileText, Users, Building2, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { searchApi, Candidate, Offer } from 'lib/api'
import { cn } from 'lib/utils'
import { useTheme } from 'contexts/ThemeContext'

interface SearchResult {
  id: number
  title: string
  subtitle?: string
  type: 'offer' | 'candidate'
  icon: React.ReactNode
  url: string
  foundIn?: string[]
}

interface GlobalSearchProps {
  isOpen: boolean
  onClose: () => void
  searchBarRef?: React.RefObject<HTMLDivElement>
  searchInputRef?: React.RefObject<HTMLInputElement>
  searchQuery: string
  onSearchQueryChange: (query: string) => void
}

export function GlobalSearch({ 
  isOpen, 
  onClose, 
  searchBarRef, 
  searchInputRef, 
  searchQuery, 
  onSearchQueryChange 
}: GlobalSearchProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = `${location.pathname}${location.search}${location.hash}`
  const { theme } = useTheme()
  const [results, setResults] = useState<{
    offers: SearchResult[]
    candidates: SearchResult[]
  }>({
    offers: [],
    candidates: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isMobile, setIsMobile] = useState(false)
  const [modalPosition, setModalPosition] = useState({ top: 0 })
  const modalRef = useRef<HTMLDivElement>(null)

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Calculate modal position for mobile
  useEffect(() => {
    if (isOpen && isMobile && searchBarRef?.current) {
      const rect = searchBarRef.current.getBoundingClientRect()
      setModalPosition({
        top: rect.bottom + 8 // 8px gap below the search bar
      })
    }
  }, [isOpen, isMobile, searchBarRef])

  // Function to strip HTML tags and return clean text
  const stripHtmlTags = (html: string): string => {
    if (!html) return ''
    // Remove HTML tags and decode HTML entities
    const temp = document.createElement('div')
    temp.innerHTML = html
    return temp.textContent || temp.innerText || ''
  }

  // Function to highlight search terms in text
  const highlightSearchTerm = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm || !text) return text
    
    const regex = new RegExp(`(${searchTerm})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className={cn(
          "px-1 rounded",
          theme === 'dark' 
            ? "bg-blue-500/20 text-blue-300" 
            : "bg-yellow-200 text-yellow-900"
        )}>
          {part}
        </span>
      ) : part
    )
  }

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Handle clicks outside the modal (desktop only - mobile uses backdrop)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isClickInsideModal = modalRef.current?.contains(target)
      const isClickInsideSearchBar = searchBarRef.current?.contains(target)
      
      if (!isMobile && isOpen && !isClickInsideModal && !isClickInsideSearchBar) {
        onClose()
      }
    }

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, isMobile])

  // Handle escape key and arrow navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        const allResults = [...results.offers, ...results.candidates]
        setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, -1))
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault()
        const allResults = [...results.offers, ...results.candidates]
        if (allResults[selectedIndex]) {
          handleResultClick(allResults[selectedIndex])
        }
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose, selectedIndex, results])

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1)
  }, [results])

  // Search function
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setResults({ offers: [], candidates: [] })
      setHasSearched(false)
      return
    }

    setIsLoading(true)
    setHasSearched(true)

    try {
      console.log('Searching for:', query)
      const response = await searchApi.globalSearch(query)
      console.log('Search response:', response.data)
      console.log('Raw response:', response)
      const { offers, candidates } = response.data
      console.log('Offers:', offers)
      console.log('Candidates:', candidates)

      // Transform offers
      const transformedOffers = offers.map(offer => ({
        id: offer.id,
        title: offer.job_title,
        subtitle: offer.department,
        type: 'offer' as const,
        icon: <FileText className="h-4 w-4" />,
        url: `/offers/${offer.id}`,
        foundIn: offer.found_in || []
      }))

      // Transform candidates
      const transformedCandidates = candidates.map(candidate => ({
        id: candidate.id,
        title: candidate.name,
        subtitle: candidate.role,
        type: 'candidate' as const,
        icon: <Users className="h-4 w-4" />,
        url: `/candidates/${candidate.id}`,
        foundIn: candidate.found_in || []
      }))

      console.log('Transformed results:', { offers: transformedOffers, candidates: transformedCandidates })

      setResults({
        offers: transformedOffers,
        candidates: transformedCandidates
      })
    } catch (error) {
      console.error('Search error:', error)
      setResults({ offers: [], candidates: [] })
    } finally {
      setIsLoading(false)
    }
  }

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'candidate') {
      navigate(result.url, { state: { from: currentPath } })
    } else {
      navigate(result.url)
    }
    onClose()
  }

  const handleFullQuery = () => {
    if (searchQuery.trim()) {
      // Navigate to a search results page or perform a more comprehensive search
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
      onClose()
    }
  }

  const testData = async () => {
    try {
      const response = await searchApi.testData()
      console.log('Test data:', response.data)
      alert(`User has ${response.data.total_offers} offers and ${response.data.total_candidates} candidates`)
    } catch (error) {
      console.error('Test data error:', error)
    }
  }

  const testSearch = async () => {
    try {
      console.log('Testing search with "a"...')
      const response = await searchApi.globalSearch('a')
      console.log('Search test response:', response.data)
      alert(`Search test found ${response.data.total_results} results`)
    } catch (error) {
      console.error('Search test error:', error)
    }
  }

  const allResults = [...results.offers, ...results.candidates]
  const totalResults = results.offers.length + results.candidates.length

  const modalContent = (
    <div 
      ref={modalRef}
      className={cn(
        "z-[10000] rounded-xl shadow-2xl max-h-[600px] overflow-hidden",
        isMobile 
          ? "fixed left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md"
          : "absolute top-full left-0 right-0 mt-2",
        theme === 'dark' 
          ? "bg-gray-900 border border-gray-700" 
          : "bg-white border border-gray-200"
      )}
      style={{
        ...(isMobile ? {
          top: `${modalPosition.top}px`
        } : {
          width: searchBarRef?.current?.offsetWidth || '100%',
          maxWidth: '1200px',
          minWidth: '1000px'
        })
      }}
    >

          {/* Search Results */}
        <div className="max-h-[500px] overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className={cn("ml-3", theme === 'dark' ? "text-gray-300" : "text-gray-600")}>Searching...</span>
              </div>
            ) : hasSearched ? (
              <div className={cn("grid gap-6", isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2")}>
              {/* Offers Column */}
                <div>
                <h3 className={cn("text-xs font-semibold uppercase tracking-wider mb-3", 
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>
                  {t('search.offers', 'OFFERS')}
                </h3>
                  {results.offers.length > 0 ? (
                    <div className="space-y-2">
                    {results.offers.slice(0, 5).map((result, index) => {
                      const globalIndex = index
                      const isSelected = selectedIndex === globalIndex
                      return (
                        <button
                          key={`offer-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className={cn(
                            "w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left group",
                            isSelected 
                              ? theme === 'dark' 
                                ? "bg-blue-900/30 border border-blue-600" 
                                : "bg-blue-50 border border-blue-200"
                              : theme === 'dark'
                                ? "hover:bg-gray-800"
                                : "hover:bg-gray-50"
                          )}
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
                            {result.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={cn("font-medium truncate", 
                              theme === 'dark' ? "text-gray-100" : "text-gray-900"
                            )}>
                              {highlightSearchTerm(result.title, searchQuery)}
                            </div>
                            {result.subtitle && (
                              <div className={cn("text-sm truncate", 
                                theme === 'dark' ? "text-gray-400" : "text-gray-500"
                              )}>
                                {highlightSearchTerm(result.subtitle, searchQuery)}
                              </div>
                            )}
                            {result.foundIn && result.foundIn.length > 0 && (
                              <div className={cn("text-xs mt-1", 
                                theme === 'dark' ? "text-blue-400" : "text-blue-600"
                              )}>
                                Found in: {result.foundIn.map(field => highlightSearchTerm(field, searchQuery)).join(', ')}
                              </div>
                            )}
                          </div>
                        </button>
                      )
                    })}
                    </div>
                  ) : (
                    <p className={cn("text-sm", 
                      theme === 'dark' ? "text-gray-500" : "text-gray-400"
                    )}>
                      {t('search.noResults', 'We have not found any result for your search.')}
                    </p>
                  )}
                </div>

              {/* Candidates Column */}
                <div>
                <h3 className={cn("text-xs font-semibold uppercase tracking-wider mb-3", 
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>
                    {t('search.candidates', 'CANDIDATES')}
                  </h3>
                  {results.candidates.length > 0 ? (
                    <div className="space-y-2">
                    {results.candidates.slice(0, 5).map((result, index) => {
                      const globalIndex = results.offers.length + index
                      const isSelected = selectedIndex === globalIndex
                      return (
                        <button
                          key={`candidate-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className={cn(
                            "w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left group",
                            isSelected 
                              ? theme === 'dark' 
                                ? "bg-blue-900/30 border border-blue-600" 
                                : "bg-blue-50 border border-blue-200"
                              : theme === 'dark'
                                ? "hover:bg-gray-800"
                                : "hover:bg-gray-50"
                          )}
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                            {result.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={cn("font-medium truncate", 
                              theme === 'dark' ? "text-gray-100" : "text-gray-900"
                            )}>
                              {highlightSearchTerm(result.title, searchQuery)}
                            </div>
                            {result.subtitle && (
                              <div className={cn("text-sm truncate", 
                                theme === 'dark' ? "text-gray-400" : "text-gray-500"
                              )}>
                                {highlightSearchTerm(result.subtitle, searchQuery)}
                              </div>
                            )}
                            {result.foundIn && result.foundIn.length > 0 && (
                              <div className={cn("text-xs mt-1", 
                                theme === 'dark' ? "text-blue-400" : "text-blue-600"
                              )}>
                                Found in: {result.foundIn.map(field => highlightSearchTerm(field, searchQuery)).join(', ')}
                              </div>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">
                    {t('search.noResults', 'We have not found any result for your search.')}
                  </p>
                )}
              </div>
            </div>
            ) : (
              <div className="text-center py-8">
                <Search className={cn("h-8 w-8 mx-auto mb-3", 
                  theme === 'dark' ? "text-gray-600" : "text-gray-300"
                )} />
                <p className={cn("text-sm", 
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>
                  {t('search.startTyping', 'Start typing to search offers and candidates')}
                </p>
              </div>
            )}
        </div>

        {/* Full Query Button */}
        {hasSearched && searchQuery.trim() && (
          <div className={cn("border-t p-4", 
            theme === 'dark' ? "border-gray-700" : "border-gray-100"
          )}>
            <button
              onClick={handleFullQuery}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {t('search.performFullQuery', 'Perform a full query')}
            </button>
          </div>
        )}
    </div>
  )

  if (!isOpen) return null

  return (
    <>
      {/* Full Page Backdrop - Rendered at document body level (mobile only) */}
      {isOpen && isMobile && createPortal(
        <div 
          className={cn(
            "fixed inset-0 z-[9999] transition-all duration-200 md:hidden",
            theme === 'dark' ? "bg-black/30" : "bg-black/20"
          )}
          onClick={onClose}
        />,
        document.body
      )}
      
      {/* Search Modal */}
      {isMobile ? createPortal(modalContent, document.body) : modalContent}
    </>
  )
}
